import React from 'react';
import { useTranslation } from 'react-i18next';

import { PageShell, BackHeader, Icon } from '../../components';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import { ACQ, CAMPAIGN_DETAIL } from '../../theme/tokens';
import type { SuggestedCampaignData } from '../../mock-data/types';
import {
  Intro,
  Period,
  IntroText,
  OfferingSection,
  OfferingPanel,
  OfferingEyebrow,
  OfferingHead,
  OfferingTitle,
  BadgeFree,
  Description,
  Channels,
  ChannelsLabel,
  ChannelsRow,
  ChannelPill,
  ChannelAvatar,
  ChannelName,
  MoreCluster,
  MoreAvatars,
  MoreAvatar,
  MoreText,
  Step,
  StepTitle,
  OptionsGrid,
  Card,
  CardHeader,
  CardImage,
  CardFooter,
  CardFooterLabel,
  ActivateInner,
  ActivateButton,
  ActivateLabel,
  Warning,
  WarningText,
} from './suggested-campaign.styles';

/** The page's data (injected by the campaign-detail dispatcher) + optional styleText/className. */
export type SuggestedCampaignProps = {
  data: SuggestedCampaignData;
  /**
   * Activate the campaign/promotion — receives the chosen style id. The
   * dispatcher flips its status to running, redirects to the section root's
   * Running tab and flags the card there for the "just activated" highlight.
   */
  onActivate?: (styleId: string) => void;
  styleText?: string;
  className?: string;
};

/** Filled (selected) or outline (unselected) heart — matches the shared Icon's heart path. */
const Heart = ({ filled, color }: { filled: boolean; color: string }): React.ReactElement => (
  <svg
    width={20}
    height={20}
    viewBox="0 0 24 24"
    fill={filled ? color : 'none'}
    stroke={filled ? 'none' : color}
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path
      d="M12 20s-7-4.6-9.3-9C1.2 8 2.6 5 5.6 5c1.9 0 3.2 1.1 3.9 2.2C10.2 6.1 11.5 5 13.4 5c3 0 4.4 3 2.9 6-2.3 4.4-9.3 9-9.3 9z"
      transform="translate(1.7 -0.5)"
    />
  </svg>
);

/**
 * Suggested campaign/promotion detail. A data-driven template shared by the
 * acquisition "Suggested Campaign" and the operations "Suggested Promotion"
 * pages (same layout, different copy/channels supplied via `data`).
 */
const SuggestedCampaign = ({
  data,
  onActivate,
  styleText = '',
  className = '',
}: SuggestedCampaignProps): React.ReactElement => {
  const { t } = useTranslation();
  const [selectedStyle, setSelectedStyle] = React.useState<string>(data.styleStep.options[0]?.id ?? '');

  // The page title and the offering "free" badge are a fixed closed UI
  // vocabulary reaching us as plain strings (from the shared mock payloads).
  // Localise them here at the render site, keyed off the stable English value,
  // so the chrome stays reactive to the live language switch.
  const TITLE_LABELS: Record<string, string> = {
    'Suggested Campaign Details': t('Suggested Campaign Details'),
    'Suggested Promotion Details': t('Suggested Promotion Details'),
  };
  const BADGE_LABELS: Record<string, string> = {
    'Free campaign': t('Free campaign'),
  };
  // The channels intro line is one of a small, fixed set of instructional
  // prompts (campaign vs promotion template), not per-record sample data.
  const CHANNELS_LABELS: Record<string, string> = {
    'Activate this campaign and get promoted on popular websites like:': t(
      'Activate this campaign and get promoted on popular websites like:',
    ),
    'Activate this promotion to advertise to players via:': t('Activate this promotion to advertise to players via:'),
  };
  const titleLabel = (value: string): string => TITLE_LABELS[value] ?? value;
  const badgeLabel = (value: string): string => BADGE_LABELS[value] ?? value;
  const channelsLabel = (value: string): string => CHANNELS_LABELS[value] ?? value;

  const onSelectStyle = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    const id = event.currentTarget.dataset.id;
    if (id) setSelectedStyle(id);
  }, []);

  const handleActivate = React.useCallback(() => {
    if (onActivate) onActivate(selectedStyle);
    else console.log('oh.module.suggested-campaign[activate]', { selectedStyle });
  }, [onActivate, selectedStyle]);

  // Activating is a build-ahead control: it writes a LOCAL status override (no backed provider
  // action yet) and the chosen style goes nowhere, so an owner could move a real campaign to
  // Running in their browser while it is still being built. Both steps ship hidden behind the
  // `campaign-activate` flag — no veil, just absent, since a veiled-but-dead button misleads —
  // leaving the page as the read-only description of what Lumio is preparing. STEP 1 goes with
  // it: choosing a style only feeds the activation it can no longer reach, and a lone "STEP 1"
  // reads as a broken flow. Revealed live for QA via `show_in_progress` (the Lumio "777" egg).
  const { isLocked } = useFeatureGate();
  const showActivation = !isLocked('campaign-activate');

  return (
    <PageShell styleText={styleText} className={className}>
      <BackHeader title={titleLabel(data.header.title)} />

      <Intro>
        <Period text={data.periodLabel} tag="span" />
        <IntroText text={data.intro} tag="span" />
      </Intro>

      <OfferingSection>
        <OfferingPanel>
          <OfferingEyebrow text="Campaign offering" tag="span" />
          <OfferingHead>
            <OfferingTitle text={data.offering.title} tag="span" />
            <BadgeFree text={badgeLabel(data.offering.badge.label)} tag="span" />
          </OfferingHead>
          <Description text={data.offering.description} tag="paragraph" />
        </OfferingPanel>

        <Channels>
          <ChannelsLabel text={channelsLabel(data.offering.channelsLabel)} tag="span" />
          <ChannelsRow>
            {data.offering.channels.map((ch) => (
              <ChannelPill key={ch.id}>
                <ChannelAvatar />
                <ChannelName text={ch.label} tag="span" />
              </ChannelPill>
            ))}
            {data.offering.moreCount > 0 && (
              <MoreCluster>
                <MoreAvatars>
                  <MoreAvatar $i={0} $bg={ACQ.white} />
                  <MoreAvatar $i={1} $bg={CAMPAIGN_DETAIL.moreAvatar2} />
                  <MoreAvatar $i={2} $bg={CAMPAIGN_DETAIL.moreAvatar3} />
                </MoreAvatars>
                <MoreText text="+{{n}} more" placeholders={{ n: data.offering.moreCount }} tag="span" />
              </MoreCluster>
            )}
          </ChannelsRow>
        </Channels>
      </OfferingSection>

      {showActivation ? (
        <>
          <Step>
            <StepTitle text="STEP 1: Choose campaign style" tag="span" />
            <OptionsGrid>
              {data.styleStep.options.map((option) => {
                const selected = selectedStyle === option.id;
                return (
                  <Card key={option.id} type="button" data-id={option.id} $selected={selected} onClick={onSelectStyle}>
                    <CardHeader text={option.label} tag="span" />
                    <CardImage $src={option.image} />
                    <CardFooter $selected={selected}>
                      <Heart filled={selected} color={selected ? ACQ.success : ACQ.orange} />
                      <CardFooterLabel text={selected ? t('Selected style') : t('Choose this style')} tag="span" />
                    </CardFooter>
                  </Card>
                );
              })}
            </OptionsGrid>
          </Step>

          <Step>
            <StepTitle text="STEP 2: Activate campaign" tag="span" />
            <ActivateInner>
              <ActivateButton type="button" onClick={handleActivate}>
                <ActivateLabel text="Activate now for free" tag="span" />
              </ActivateButton>
              <Warning>
                <Icon name="warning" size={14} color={ACQ.orange} />
                <WarningText text={data.warning} tag="span" />
              </Warning>
            </ActivateInner>
          </Step>
        </>
      ) : null}
    </PageShell>
  );
};

export default SuggestedCampaign;
